/**
 * Pure display helpers.
 *
 * Their own module rather than exports beside the components that use them, for
 * two reasons that point the same way. `react-refresh/only-export-components`
 * — a warning, and `eslint --max-warnings=0` makes warnings fatal — refuses a
 * module exporting both a component and a function. And every one of these is
 * a decision worth a test of its own: what a mobile number looks like, what a
 * user agent is called, when the evening starts. Testing them through a
 * rendered component would be testing them through three layers of nothing.
 */

/** Initials for an avatar — at most two, from the first two words. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  if (words.length === 0) return '?'
  return words.map((word) => (word[0] ?? '').toUpperCase()).join('')
}

/**
 * Whether what was typed into the sign-in field is a phone number.
 *
 * The API takes an email *or* a mobile (`LoginRequest`) and S-D2 draws a
 * toggle, but a toggle is one more thing to get wrong; "optional +, then digits
 * and separators" is unambiguous for the two formats §4 accepts. A wrong guess
 * costs nothing — the answer to both is the same `INVALID_CREDENTIALS`.
 */
export function looksLikeMobile(value: string): boolean {
  return /^\+?[0-9 ()-]{6,}$/.test(value.trim())
}

/** A first guess at an organisation's short name. Always shown before it is used. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/** Times of day in the tenant's zone, never the browser's or the server's. */
export function greeting(now: Date, timeZone: string): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone }).format(now),
  )
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/** The part of an address before the @, as something to greet a person by. */
export function displayName(email: string | null | undefined, mobile: string | null): string {
  if (email) {
    const local = email.split('@')[0] ?? email
    const first = local.split(/[._-]/)[0] ?? local
    return first.charAt(0).toUpperCase() + first.slice(1)
  }
  return mobile ?? 'there'
}

/** A timestamp as a date a person reads. */
export function formatDate(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * A timestamp as "how long ago" — or "how long until", when it has not happened
 * yet.
 *
 * Both directions, because half the call sites are counting *forward*. A
 * LinkedIn authorisation ends in sixty days and a pairing code expires in ten
 * minutes; only the sessions list and the device table look backwards.
 * Measuring the distance without the direction made every forward-looking one
 * read "Just now" — the difference is negative, and a negative number is below
 * every threshold — so `ConnectionsPanel`'s whole reason for naming the expiry
 * instead of waiting for a post to fail was answered with "Just now", and a
 * pairing code with its full ten minutes left announced itself as spent.
 *
 * "Just now" stays symmetric. Inside ninety seconds either way the direction is
 * noise, and "in 1 minute" is a worse answer than "Just now" for something that
 * is, to the person reading it, happening.
 */
export function relativeTime(iso: string, now: Date): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return iso
  const signed = Math.round((now.getTime() - then.getTime()) / 1000)
  const seconds = Math.abs(signed)
  if (seconds < 90) return 'Just now'
  // The sign picks the wording; every threshold below reads the distance, which
  // is why they are unchanged from when this only counted backwards.
  const past = signed > 0
  const say = (value: number, unit: string): string => {
    const amount = `${String(value)} ${unit}${value === 1 ? '' : 's'}`
    return past ? `${amount} ago` : `in ${amount}`
  }
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return say(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (hours < 24) return say(hours, 'hour')
  return say(Math.round(hours / 24), 'day')
}

/**
 * A clock time, in the organisation's own zone.
 *
 * The reasoning trail's left column. Seconds included, because two steps of a
 * chain routinely land in the same minute and a column of identical times is a
 * column that answers nothing — the prototype's own trail runs *04:00:02 →
 * 04:00:14 → 04:01:38*.
 *
 * `timeZone` is required rather than defaulted to the browser's, which is the
 * same call `greeting` makes and the same rule CLAUDE.md states for the API:
 * every time in this product is the tenant's, and the machine a page happens to
 * be open on is not a source of truth about when a run happened.
 */
export function formatTime(iso: string, timeZone: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone,
  }).format(parsed)
}

/**
 * A timestamp as a day and a clock time, both in the organisation's zone.
 *
 * `formatDate` renders in the **browser's** zone, which is right for everything
 * that pairs a date with a relative age and wrong here: the reasoning trail puts
 * a date beside a `formatTime` clock, and two different zones on one line
 * produce *"23 Jul, 04:00"* for a run that, in the browser's zone, happened on
 * the 22nd. One helper, one zone, so the pair cannot disagree.
 */
export function formatDateIn(iso: string, timeZone: string): string {
  /* A day, in the organisation's zone, with no clock beside it.
   *
   * `formatDate` reads the **browser's** zone, which is wrong wherever the date
   * has to agree with a decision the server made in the tenant's. S-B33's
   * content-impact table is exactly that: the server decides
   * `published_in_window` from the tenant's own dates, so a row rendered in the
   * browser's zone can carry a *New* badge beside a date outside the period it
   * was badged for — one line asserting two contradictory things, which is the
   * defect S-B32c found in the reasoning trail in its other form.
   *
   * Separate from `formatDateTime` rather than a flag on it: a table row wants a
   * day, and a clock time nobody asked for is a column's worth of noise on every
   * row.
   */
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(parsed)
}

export function formatDateTime(iso: string, timeZone: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone,
  }).format(parsed)
}

/**
 * How long something took — *"6m 12s"*, *"1h 04m"*, *"820ms"*.
 *
 * Two units at most, and never a unit smaller than a hundredth of the total:
 * *"1h 04m 09s"* is three facts where the third changes nothing a reader would
 * do, and the prototype's own badge reads *"Completed in 6m 12s"*.
 *
 * **Sub-second durations keep their milliseconds** rather than rounding to
 * *"0s"*. A cancelled pass that ended before it started is a real row — the
 * task was stopped between the dispatch and the first stage — and *"0s"* reads
 * as a measurement that failed rather than as one that is genuinely tiny.
 *
 * A negative span returns null. `started_at` and `finished_at` are written by
 * two different processes (`app/db/runs.py` — the executor and the sweep race
 * to end a run), so a clock skew of a few milliseconds is possible, and *"-2s"*
 * on a card is worse than no badge.
 */
export function formatDuration(fromIso: string, toIso: string): string | null {
  const from = new Date(fromIso).getTime()
  const to = new Date(toIso).getTime()
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null

  const total = Math.round((to - from) / 1000)
  if (total === 0) return `${String(to - from)}ms`
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  if (hours > 0) return `${String(hours)}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${String(minutes)}m ${String(seconds).padStart(2, '0')}s`
  return `${String(seconds)}s`
}

/**
 * A browser string as something a person recognises.
 *
 * Deliberately coarse. A full user agent is unreadable, and the question the
 * sessions panel answers is "is one of these not me?" — for which "Chrome on
 * macOS" is enough and a version number is noise. An agent that matches nothing
 * is shown verbatim rather than guessed at: this is the screen somebody uses to
 * decide whether they have been broken into, and a confident wrong label there
 * is worse than an unfamiliar true one.
 *
 * Order matters. Every Chromium browser carries `Chrome/` and every one of them
 * carries `Safari/`, so the specific tokens are tested first.
 */
export function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device'

  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : /curl\//.test(userAgent)
              ? 'curl'
              : null

  const platform = /iPhone|iPad/.test(userAgent)
    ? 'iOS'
    : /Android/.test(userAgent)
      ? 'Android'
      : /Mac OS X/.test(userAgent)
        ? 'macOS'
        : /Windows/.test(userAgent)
          ? 'Windows'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null

  if (!browser && !platform) return userAgent.slice(0, 60)
  if (!platform) return browser ?? userAgent.slice(0, 60)
  if (!browser) return platform
  return `${browser} on ${platform}`
}
